import { useEffect, useRef } from 'react';

/**
 * Animated waveform visualization for voice recording
 * Shows real-time audio levels with smooth bars
 */
function VoiceWaveform({
  audioLevel = 0,
  isRecording = false,
  className = '',
  barsCount = 40,
  barMaxHeight = 60
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const barsRef = useRef([]);
  const timeRef = useRef(0);

  const BARS_COUNT = barsCount; // Number of bars in the waveform
  const BAR_WIDTH = 3;
  const BAR_GAP = 2;
  const BAR_MIN_HEIGHT = 4;
  const BAR_MAX_HEIGHT = barMaxHeight;
  const NOISE_FLOOR = 5;
  const SPEECH_RANGE = 30;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    const width = (BAR_WIDTH + BAR_GAP) * BARS_COUNT;
    const height = BAR_MAX_HEIGHT + 10;
    
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    
    ctx.scale(dpr, dpr);

    // Initialize bars with random heights
    if (barsRef.current.length !== BARS_COUNT) {
      barsRef.current = Array.from({ length: BARS_COUNT }, () => ({
        height: BAR_MIN_HEIGHT,
        targetHeight: BAR_MIN_HEIGHT,
        speed: 0.15 + Math.random() * 0.15
      }));
    }

    const animate = () => {
      timeRef.current += 0.05;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      const rawLevel = Math.max(0, Math.min(100, audioLevel));
      const speechLevel = Math.max(0, Math.min(1, (rawLevel - NOISE_FLOOR) / SPEECH_RANGE));
      const voiceResponse = Math.pow(speechLevel, 0.55);

      // Update and draw each bar
      barsRef.current.forEach((bar, index) => {
        // Calculate target height based on audio level and wave pattern
        if (isRecording) {
          const center = (BARS_COUNT - 1) / 2;
          const distanceFromCenter = Math.abs(index - center) / Math.max(1, center);
          const centerWeight = 1 - distanceFromCenter;
          const waveOffset = Math.sin(timeRef.current * 2.2 + index * 0.48) * 0.16;

          if (voiceResponse > 0.02) {
            const shapedBar = 0.45 + centerWeight * 0.55 + waveOffset;
            const targetRange = BAR_MAX_HEIGHT - BAR_MIN_HEIGHT;
            bar.targetHeight = BAR_MIN_HEIGHT + targetRange * voiceResponse * Math.max(0.22, shapedBar);
          } else {
            // Stay nearly flat when the room is quiet.
            bar.targetHeight = BAR_MIN_HEIGHT + Math.max(0, Math.sin(timeRef.current + index * 0.35)) * 0.7;
          }
        } else {
          // Idle state - almost still, just enough to feel alive.
          bar.targetHeight = BAR_MIN_HEIGHT + Math.max(0, Math.sin(timeRef.current + index * 0.5)) * 1.2;
        }

        // Smooth transition to target height
        const easing = isRecording && voiceResponse > 0.02 ? 0.36 : bar.speed;
        bar.height += (bar.targetHeight - bar.height) * easing;

        // Draw bar
        const x = index * (BAR_WIDTH + BAR_GAP);
        const barHeight = Math.max(BAR_MIN_HEIGHT, bar.height);
        const y = (height - barHeight) / 2;

        // Gradient based on height and position
        const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
        
        if (isRecording) {
          // Warm plum gradient when recording
          const intensity = bar.height / BAR_MAX_HEIGHT;
          gradient.addColorStop(0, `rgba(176, 160, 192, ${0.9 + intensity * 0.1})`);
          gradient.addColorStop(0.5, `rgba(124, 106, 144, ${0.82 + intensity * 0.18})`);
          gradient.addColorStop(1, `rgba(214, 190, 165, ${0.72 + intensity * 0.22})`);
        } else {
          // Quiet plum gradient when idle
          gradient.addColorStop(0, 'rgba(124, 106, 144, 0.62)');
          gradient.addColorStop(1, 'rgba(176, 160, 192, 0.42)');
        }

        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, BAR_WIDTH, barHeight);

        // Add glow effect for taller bars
        if (isRecording && bar.height > BAR_MAX_HEIGHT * 0.5) {
          ctx.shadowColor = isRecording ? 'rgba(124, 106, 144, 0.45)' : 'rgba(176, 160, 192, 0.35)';
          ctx.shadowBlur = 8;
          ctx.fillRect(x, y, BAR_WIDTH, barHeight);
          ctx.shadowBlur = 0;
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioLevel, isRecording, BARS_COUNT, BAR_MAX_HEIGHT]);

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <canvas
        ref={canvasRef}
        className="rounded-lg"
        style={{ imageRendering: 'crisp-edges' }}
      />
    </div>
  );
}

export default VoiceWaveform;
